/**
 * 확인자 지정
 * ---------------------------------------------------------------------------
 * 법정 일지는 작성자와 확인자가 다른 것이 정상이고, 확인자가 여러 명일 때가
 * 많다. 결재란이 칸 여러 개로 그려져 있는 이유다.
 *
 * 순서가 있다
 *   결재란에 왼쪽부터 놓이는 순서다. 위·아래 버튼으로 바꾼다. 드래그가
 *   보기에는 낫지만, 현장 단말에서 터치로 끌어 놓는 조작은 자주 빗나간다.
 *
 * 순서대로만 확인하게 하지는 않는다
 *   앞사람이 휴가면 결재가 멈추고, 그러면 종이로 돌아간다. 순서는 표시
 *   순서이고 확인은 각자 한다.
 *
 * 이미 확인한 사람
 *   지울 수 없게 둔다. 결재를 받은 뒤 그 사람을 목록에서 빼면 인쇄물의
 *   결재란과 화면이 달라진다.
 */

import { useQuery } from '@tanstack/react-query';
import { Button, Card, Empty, Select, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import type { LogApprover, Member } from '@eco/shared';
import { api } from '../../lib/api';

const { Text } = Typography;

export function ApproverPicker({
  approvers,
  siteId,
  locked,
  onChange,
}: {
  approvers: LogApprover[];
  /** 사업장 기본 확인자를 불러오기 위해 필요하다. */
  siteId: string | undefined;
  locked: boolean;
  onChange: (approvers: LogApprover[]) => void;
}) {
  const members = useQuery<Member[]>({
    queryKey: ['members'],
    queryFn: async () => (await api.get<{ items: Member[] }>('/daily-logs/members')).data.items,
  });

  /**
   * 사업장 기본 확인자.
   *
   * 새 일지에는 서버가 자동으로 넣어 주지만, **이미 만들어진 일지**에는
   * 들어가지 않는다. 사업장에 기본 확인자를 나중에 등록한 경우가 그렇다 —
   * 그때 손으로 다시 고르게 하지 않으려고 불러오기를 둔다.
   */
  const siteDefaults = useQuery<LogApprover[]>({
    queryKey: ['site-approvers', siteId],
    queryFn: async () => (await api.get<LogApprover[]>(`/sites/${siteId}/approvers`)).data,
    enabled: !!siteId,
    // 권한이 없으면 조용히 넘어간다. 불러오기 버튼만 안 보인다.
    retry: false,
  });

  const chosen = new Set(approvers.map((a) => a.userId));

  /** 기본 확인자를 더한다. 이미 있는 사람과 확인한 사람은 건드리지 않는다. */
  const applyDefaults = () => {
    const added = (siteDefaults.data ?? []).filter((d) => !chosen.has(d.userId));
    if (added.length === 0) return;
    onChange(
      [...approvers, ...added.map((d) => ({ ...d, confirmedAt: null }))].map((a, i) => ({
        ...a,
        seq: i,
      })),
    );
  };

  const add = (userId: string) => {
    const member = members.data?.find((m) => m.userId === userId);
    if (!member || chosen.has(userId)) return;
    onChange(
      [...approvers, { userId, name: member.name, seq: approvers.length, confirmedAt: null }].map(
        (a, i) => ({ ...a, seq: i }),
      ),
    );
  };

  const removeAt = (index: number) => {
    onChange(approvers.filter((_, i) => i !== index).map((a, i) => ({ ...a, seq: i })));
  };

  const swap = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= approvers.length) return;
    const next = [...approvers];
    [next[index], next[target]] = [next[target]!, next[index]!];
    onChange(next.map((a, i) => ({ ...a, seq: i })));
  };

  const confirmedCount = approvers.filter((a) => a.confirmedAt).length;

  return (
    <Card
      title="확인자 (결재)"
      size="small"
      extra={
        !locked && (
          <Space>
            {(siteDefaults.data ?? []).some((d) => !chosen.has(d.userId)) && (
              <Button size="small" onClick={applyDefaults}>
                사업장 기본 확인자 불러오기
              </Button>
            )}
            <Select
              showSearch
              placeholder="구성원에서 확인자 추가"
              style={{ width: 260 }}
              value={null}
              loading={members.isLoading}
              optionFilterProp="label"
              onChange={add}
              notFoundContent={members.isError ? '구성원을 불러오지 못했습니다' : undefined}
              options={(members.data ?? [])
                .filter((m) => !chosen.has(m.userId))
                .map((m) => ({
                  value: m.userId,
                  // 서명을 등록하지 않은 사람은 결재란에 이름만 찍힌다.
                  label: m.hasSignature ? m.name : `${m.name} (서명 미등록)`,
                }))}
            />
          </Space>
        )
      }
    >
      {approvers.length === 0 ? (
        <Empty
          image={null}
          description={
            <Text type="secondary">
              확인자를 지정하지 않으면 결재란의 확인자 칸이 비고, 확인 권한이 있는 사람이면
              누구나 [확인자 확인하기] 로 확인할 수 있습니다.
              <br />
              작성자 한 사람이 쓰고 확인하는 작은 사업장은 이대로 두어도 됩니다.
              <br />
              여러 명이 순서대로 결재한다면 확인자를 지정하세요. 사업장·시설 화면에 기본
              확인자를 등록해 두면 새 일지에 자동으로 들어옵니다.
            </Text>
          }
        />
      ) : (
        <>
          <Table<LogApprover>
            className="eco-grid"
            rowKey="userId"
            size="small"
            pagination={false}
            dataSource={approvers}
            columns={[
              { title: '순서', width: 60, render: (_, __, i) => i + 1 },
              { title: '이름', dataIndex: 'name' },
              {
                title: '확인',
                width: 180,
                render: (_, row) =>
                  row.confirmedAt ? (
                    <Tag color="green">{new Date(row.confirmedAt).toLocaleString('ko-KR')}</Tag>
                  ) : (
                    <Tag>대기</Tag>
                  ),
              },
              {
                title: '',
                width: 150,
                render: (_, row, i) =>
                  !locked && (
                    <Space size={0}>
                      <Tooltip title="위로">
                        <Button
                          type="link"
                          size="small"
                          icon={<UpOutlined />}
                          disabled={i === 0}
                          onClick={() => swap(i, -1)}
                        />
                      </Tooltip>
                      <Tooltip title="아래로">
                        <Button
                          type="link"
                          size="small"
                          icon={<DownOutlined />}
                          disabled={i === approvers.length - 1}
                          onClick={() => swap(i, 1)}
                        />
                      </Tooltip>
                      <Tooltip title={row.confirmedAt ? '이미 확인한 사람은 뺄 수 없습니다' : ''}>
                        <Button
                          type="link"
                          size="small"
                          danger
                          disabled={!!row.confirmedAt}
                          onClick={() => removeAt(i)}
                        >
                          삭제
                        </Button>
                      </Tooltip>
                    </Space>
                  ),
              },
            ]}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            확인자 {approvers.length}명 중 {confirmedCount}명이 확인했습니다. 전원이 확인하면
            확인완료가 됩니다.
          </Text>
        </>
      )}
    </Card>
  );
}
